import { createSlice } from '@reduxjs/toolkit';
import { getPersistedDjSidebarWidth } from '../utils/djSidebarWidth';

// Initial state with defaults - persisted state will be loaded after mount
const initialState = {
    sidebarOpen: false,
    sidebarWidth: getPersistedDjSidebarWidth(),
    syncStatus: 'not_synced', // 'not_synced' | 'checking' | 'synced' | 'partial' (import stopped/failed, whole playlists kept) | 'error'
    seratoPath: null,
    seratoExists: null,
    seratoPlaylists: [], // Playlists from last import
    lastSyncTime: null,
    importing: false,
    exporting: false,
    error: null,
    persistedStateLoaded: false
};

const seratoSlice = createSlice({
    name: 'serato',
    initialState,
    reducers: {
        loadPersistedSeratoState(state, action) {
            // Only load once
            if (state.persistedStateLoaded) return;

            // If payload provided (from async load), use it
            if (action.payload) {
                const { lastSyncTime, seratoPath, seratoPlaylists } = action.payload;
                if (lastSyncTime) {
                    state.lastSyncTime = lastSyncTime;
                    state.syncStatus = 'synced';
                }
                if (seratoPath) {
                    state.seratoPath = seratoPath;
                }
                if (seratoPlaylists && seratoPlaylists.length > 0) {
                    state.seratoPlaylists = seratoPlaylists;
                }
            }

            state.persistedStateLoaded = true;
        },
        toggleSeratoSidebar(state) {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setSeratoSidebarOpen(state, action) {
            state.sidebarOpen = action.payload;
        },
        setSeratoSidebarWidth(state, action) {
            state.sidebarWidth = action.payload;
        },
        setSeratoSyncStatus(state, action) {
            state.syncStatus = action.payload;
        },
        setSeratoPath(state, action) {
            state.seratoPath = action.payload;
        },
        setSeratoExists(state, action) {
            state.seratoExists = action.payload;
        },
        setSeratoPlaylists(state, action) {
            state.seratoPlaylists = action.payload;
        },
        setSeratoLastSyncTime(state, action) {
            state.lastSyncTime = action.payload;
        },
        setSeratoImporting(state, action) {
            state.importing = action.payload;
        },
        setSeratoExporting(state, action) {
            state.exporting = action.payload;
        },
        setSeratoError(state, action) {
            state.error = action.payload;
        },
        // Owner-scoped "Last synced" from the main process (djlibrary:getSyncState),
        // fetched on boot and on every account change by DjSyncStateHydrator.
        // Null = this account never synced this app: leave the state alone.
        hydrateSeratoSyncState(state, action) {
            const at = action.payload;
            if (!at) return;
            state.lastSyncTime = at;
            if (state.syncStatus === 'not_synced') state.syncStatus = 'synced';
        },
        resetSeratoState(state) {
            state.syncStatus = 'not_synced';
            state.seratoPlaylists = [];
            state.lastSyncTime = null;
            state.error = null;
        }
    },
});

export const {
    loadPersistedSeratoState,
    toggleSeratoSidebar,
    setSeratoSidebarOpen,
    setSeratoSidebarWidth,
    setSeratoSyncStatus,
    setSeratoPath,
    setSeratoExists,
    setSeratoPlaylists,
    setSeratoLastSyncTime,
    setSeratoImporting,
    setSeratoExporting,
    setSeratoError,
    hydrateSeratoSyncState,
    resetSeratoState
} = seratoSlice.actions;

export default seratoSlice.reducer;
